/**
 * CheckboxGroupWithCustom — 다중 선택 체크박스 + "기타 (직접 입력)" 칩 추가.
 *
 * value 는 string[] — 선택된 옵션 value 들 + 사용자 직접 입력한 임의 항목들.
 * 사용자 직접 항목은 옵션에 없는 string 으로 식별됨.
 */
import { useState } from 'react'
import { FiPlus, FiX } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'

export interface CheckOption {
  value: string
  label: string
}

interface Props {
  label?: string
  options: readonly CheckOption[] | CheckOption[]
  value: string[]
  onChange: (next: string[]) => void
  customPlaceholder?: string
  required?: boolean
  className?: string
  disabled?: boolean
  columns?: 2 | 3 | 4 | 5 | 6
  /** 추천 옵션 value 목록 — 상단 배치 + amber 하이라이트 + ✨ 배지. */
  recommendedKeys?: string[]
}

export default function CheckboxGroupWithCustom({
  label,
  options,
  value,
  onChange,
  customPlaceholder,
  required,
  className,
  disabled,
  columns = 3,
  recommendedKeys,
}: Props) {
  const { t } = useTranslation()
  const resolvedCustomPlaceholder = customPlaceholder ?? t('commonComponents.checkboxGroupWithCustom.customPlaceholder', { defaultValue: '직접 입력 후 Enter' })
  const [draft, setDraft] = useState<string>('')

  const optionValues = new Set(options.map((o) => o.value))
  const customSelected = value.filter((v) => !optionValues.has(v))

  // 추천 우선 정렬 — 추천 옵션을 상단으로, 추천 내부 순서는 recommendedKeys 순서 따름.
  const recSet = new Set(recommendedKeys ?? [])
  const sortedOptions = recSet.size > 0
    ? [
        ...(recommendedKeys ?? []).map((k) => options.find((o) => o.value === k)).filter((o): o is CheckOption => !!o),
        ...options.filter((o) => !recSet.has(o.value)),
      ]
    : [...options]

  const toggle = (v: string) => {
    if (disabled) return
    if (value.includes(v)) onChange(value.filter((x) => x !== v))
    else onChange([...value, v])
  }

  const addCustom = () => {
    const v = draft.trim()
    if (!v) return
    if (value.includes(v)) {
      setDraft('')
      return
    }
    onChange([...value, v])
    setDraft('')
  }

  const removeCustom = (v: string) => {
    onChange(value.filter((x) => x !== v))
  }

  const gridCols =
    columns === 6 ? 'sm:grid-cols-3 lg:grid-cols-6' :
    columns === 5 ? 'sm:grid-cols-3 lg:grid-cols-5' :
    columns === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' :
    columns === 3 ? 'sm:grid-cols-3' :
    'sm:grid-cols-2'

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <label className="text-xs text-ink-muted font-medium">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div className={cn('grid grid-cols-2 gap-2', gridCols)}>
        {sortedOptions.map((opt) => {
          const checked = value.includes(opt.value)
          const recommended = recSet.has(opt.value)
          return (
            <label
              key={opt.value}
              className={cn(
                'relative flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs transition-colors',
                checked
                  ? 'border-brand-primary bg-brand-primary/10 text-ink'
                  : recommended
                    ? 'border-amber-400/60 bg-amber-400/10 text-ink hover:border-amber-400 hover:bg-amber-400/15 ring-1 ring-amber-400/30'
                    : 'border-surface-border bg-surface-card text-ink-muted hover:border-brand-primary/50',
                disabled && 'opacity-60 cursor-not-allowed',
              )}
              title={recommended ? t('commonComponents.checkboxGroupWithCustom.recommendedTitle', { defaultValue: '상위 선택 기반 추천' }) : undefined}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.value)}
                disabled={disabled}
                className="h-3.5 w-3.5 accent-brand-primary"
              />
              <span className="truncate flex-1">{opt.label}</span>
              {recommended && (
                <span className="text-[9px] leading-none px-1 py-0.5 rounded bg-amber-500/30 text-zinc-900 dark:text-white dark:text-zinc-900 dark:text-white border border-amber-500/40 shrink-0" aria-label={t('commonComponents.checkboxGroupWithCustom.recommended', { defaultValue: '추천' })}>
                  ✨
                </span>
              )}
            </label>
          )
        })}
      </div>

      {/* 기타 직접 입력 칩 */}
      {customSelected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {customSelected.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-amber-500/15 text-zinc-900 dark:text-white dark:text-zinc-900 dark:text-white border border-amber-500/30"
            >
              {v}
              <button
                type="button"
                onClick={() => removeCustom(v)}
                disabled={disabled}
                className="hover:text-red-500"
                aria-label={t('commonComponents.checkboxGroupWithCustom.removeAria', { value: v, defaultValue: '{{value}} 제거' })}
              >
                <FiX size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addCustom()
            }
          }}
          placeholder={resolvedCustomPlaceholder}
          disabled={disabled}
          className="flex-1 px-3 py-1.5 rounded-lg border border-surface-border bg-surface-card text-xs text-ink placeholder:text-ink-faint focus:outline-none focus:ring-1 focus:ring-brand-primary"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={disabled || !draft.trim()}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface-elevated hover:bg-brand-primary hover:text-white text-xs text-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <FiPlus size={12} /> {t('commonComponents.checkboxGroupWithCustom.add', { defaultValue: '추가' })}
        </button>
      </div>
    </div>
  )
}
